import { Autocomplete, Box, Chip, Icon, ListItemText, Menu, MenuItem, TextField } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import { dateFormat } from '../../../../helpers/dateFormat';
import { LayoutRegister } from '../../../../layouts';
import { appointmentService, feedback, finishRequest, IAppointmentView, IUpdateAppointment } from '../../../../services';
import { Api } from '../../../../services/api/axios-config';
import { setCsrfRequestToken } from '../../../../services/api/axios-config/interceptors';
import { Button } from '../../../MUI/button/Button';
import { DeleteConfirmationDialog } from '../../deleteConfirmation/DeleteConfirmationDialog';

interface IEventDetailsProps {
  appointment: IAppointmentView;
  refetch: () => any;
  refetchAppointments: () => any;
  onClose: () => any;
}

export const EventDetails: React.FC<IEventDetailsProps> = ({ appointment, refetch, refetchAppointments, onClose }) => {
  const [openDialogCancel, setOpenDialogCancel] = useState(false);
  const [anchorElWhatsApp, setAnchorElWhatsApp] = useState<null | HTMLElement>(null);
  const predefinedMessages: string[] = (() => {
    try { return JSON.parse(appointment.predefinedMessages ?? '[]'); }
    catch { return []; }
  })();

  const { mutate: cancel, isLoading: isLoadingCancel } = useMutation(async (appointment: IUpdateAppointment) => {
    try {
      const tokenRes = await Api.get('/auth/antiforgery-token');
      if (tokenRes.data) setCsrfRequestToken(tokenRes.data as string);
    } catch {
      // best-effort
    }
    return appointmentService.update(appointment);
  }, {
    onSuccess: () => finishRequest('Agendamento caneclado', 'success', () => {
      refetch();
      refetchAppointments();
      setOpenDialogCancel(false);
      onClose();
    }),
    onError: (error: any) => finishRequest(String(error), 'error'),
  });

  const { mutate: updateStatus, isLoading: isLoadingStatus } = useMutation(
    async (status: number) => {
      try {
        const tokenRes = await Api.get('/auth/antiforgery-token');
        if (tokenRes.data) setCsrfRequestToken(tokenRes.data as string);
      } catch {
        // best-effort
      }
      return appointmentService.updateStatus(appointment.id, status);
    },
    {
      onSuccess: () => finishRequest('Status atualizado', 'success', () => {
        refetch();
        refetchAppointments();
        onClose();
      }),
      onError: (error: any) => feedback(String(error), 'error'),
    }
  );

  const handleConfirmCancel = useCallback(async () => {
    const submitData: IUpdateAppointment = {
      id: appointment.id,
      canceled: true,
      date: appointment.date,
      businessId: appointment.businessId,
      businessEmployeeId: appointment.businessEmployeeId,
      services: appointment.services.map(x => ({ serviceId: x.id })),
      products: appointment.products.map(x => ({ productId: x.id })),
      userId: appointment.userId,
    };

    cancel(submitData);
  }, []);

  const statusLabel = appointment.status === 1 ? 'Pendente' : appointment.status === 2 ? 'Rejeitado' : appointment.status === 3 ? 'Cancelado' : 'Confirmado';
  const statusColor = appointment.status === 1 ? 'warning' : appointment.status === 2 ? 'error' : appointment.status === 3 ? 'default' : 'success';

  return (
    <LayoutRegister header='Agendamento' isLoading={false} hideSaveButton>
      <Box display='flex' flexDirection='column' gap={2}>
        <TextField
          label='Cliente'
          value={appointment.customerName}
          disabled
        />

        <Autocomplete
          multiple
          getOptionLabel={opt => `${opt.description} - R$${opt.amount}`}
          options={appointment.services}
          value={appointment.services}
          disabled
          renderInput={(params) => (
            <TextField
              {...params}
              label='Serviços'
              variant='outlined'
            />
          )}
        />

        <TextField
          label='Data'
          value={dateFormat(appointment.date, 'LLL')}
          disabled
        />

        <Box display='flex' alignItems='center' gap={1}>
          <Chip label={statusLabel} color={statusColor} size='small' />
        </Box>

        {appointment.status === 1 && !appointment.past ? (
          <Box display='flex' gap={1}>
            <Button
              label='Aprovar'
              variant='contained'
              color='success'
              fullWidth
              onClick={() => updateStatus(0)}
              isLoading={isLoadingStatus}
            />
            <Button
              label='Rejeitar'
              variant='outlined'
              color='error'
              fullWidth
              onClick={() => updateStatus(2)}
              isLoading={isLoadingStatus}
            />
          </Box>
        ) : (
          !appointment.past &&
          <Button label='Cancelar' onClick={() => setOpenDialogCancel(true)} />
        )}

        {appointment.customerPhone && (
          <>
            <Button
              label='WhatsApp'
              variant='outlined'
              color='success'
              fullWidth
              startIcon={<Icon>chat</Icon>}
              onClick={(e) => setAnchorElWhatsApp(e.currentTarget)}
            />
            <Menu
              anchorEl={anchorElWhatsApp}
              open={Boolean(anchorElWhatsApp)}
              onClose={() => setAnchorElWhatsApp(null)}
            >
              {predefinedMessages.length > 0 ? (
                predefinedMessages.map((msg, idx) => (
                  <MenuItem
                    key={idx}
                    onClick={() => {
                      const phone = appointment.customerPhone!.replace(/\D/g, '');
                      window.open(
                        `https://wa.me/55${phone}?text=${encodeURIComponent(msg)}`,
                        '_blank'
                      );
                      setAnchorElWhatsApp(null);
                    }}
                  >
                    <ListItemText
                      primary={msg.length > 60 ? msg.substring(0, 60) + '…' : msg}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </MenuItem>
                ))
              ) : (
                <MenuItem
                  onClick={() => {
                    const phone = appointment.customerPhone!.replace(/\D/g, '');
                    window.open(`https://wa.me/55${phone}`, '_blank');
                    setAnchorElWhatsApp(null);
                  }}
                >
                  <ListItemText primary='Enviar WhatsApp' />
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </Box>

      <DeleteConfirmationDialog
        isLoading={isLoadingCancel}
        onClose={() => setOpenDialogCancel(false)}
        onConfirm={handleConfirmCancel}
        open={openDialogCancel}
        label='Tem certeza que deseja cancelar este agendamento?'
      />
    </LayoutRegister>
  );
};